import React, { useState } from "react";
import { connect } from "react-redux";
import PhoneInputWithCountrySelect from "react-phone-number-input";
import { Modal, ModalHeader, ModalBody, Form, Input } from "reactstrap";
import axios from "../../../axios";
import * as actionCreators from "../../../modules/actions/index";
import VerificationCodeEntry from "../../../components/VerificationCodeEntry";
import * as url from "../../../utils/url";
import { saveToken } from "../../../utils/localStorage";
import { validateEmail } from "../../../utils/validations";
import {
  TRIVIA_LOGIN_MODAL_TITLE,
  TRIVIA_LOGIN_MODAL_SUBTITLE_CHOOSE,
  TRIVIA_LOGIN_MODAL_SUBTITLE_EMAIL,
  TRIVIA_LOGIN_MODAL_SUBTITLE_PHONE,
  TRIVIA_LOGIN_MODAL_SUBTITLE_CODE,
  TRIVIA_LOGIN_OPTION_EMAIL,
  TRIVIA_LOGIN_OPTION_PHONE,
  TRIVIA_LOGIN_EMAIL_LABEL,
  TRIVIA_LOGIN_PASSWORD_LABEL,
  TRIVIA_LOGIN_PHONE_LABEL,
  TRIVIA_LOGIN_SUBMIT,
  TRIVIA_LOGIN_SEND_CODE,
  TRIVIA_LOGIN_BACK,
  TRIVIA_LOGIN_EMAIL_PLACEHOLDER,
  TRIVIA_LOGIN_PASSWORD_PLACEHOLDER,
  TRIVIA_LOGIN_PHONE_PLACEHOLDER,
  TRIVIA_LOGIN_NOT_INTENDED,
  TRIVIA_LOGIN_INVALID_CREDENTIALS,
  TRIVIA_LOGIN_INVALID_CODE,
  TRIVIA_LOGIN_GENERIC_ERROR,
} from "../../../utils/constants";
import "./triviaLoginModal.css";

const STEP_CHOOSE = "choose";
const STEP_EMAIL = "email";
const STEP_PHONE = "phone";
const STEP_PHONE_CODE = "phoneCode";

const TriviaLoginModal = ({
  isOpen,
  orgId,
  history,
  onAuthSuccess,
  onLoginSuccess,
}) => {
  const [step, setStep] = useState(STEP_CHOOSE);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const resetTransientState = () => {
    setIsLoading(false);
    setErrorMessage(null);
  };

  const goToChoose = () => {
    setStep(STEP_CHOOSE);
    resetTransientState();
  };

  const goToEmail = () => {
    setStep(STEP_EMAIL);
    resetTransientState();
  };

  const goToPhone = () => {
    setStep(STEP_PHONE);
    resetTransientState();
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!validateEmail(email) || !password) {
      setErrorMessage(TRIVIA_LOGIN_INVALID_CREDENTIALS);
      return;
    }
    setIsLoading(true);
    setErrorMessage(null);

    const payload = {
      email: email.toLowerCase(),
      password: password,
    };

    axios
      .post(url.loginUser, payload, {
        headers: {
          Authorization: "Bearer basicauth",
        },
      })
      .then((response) => {
        const data = response && response.data;
        // MFA path: BE returns { pendingMFA: true, email, userDetails } with NO
        // autherizationToken when the user's org has MFA enabled and the user
        // is an Admin. Mirror Login.jsx's behavior: stash mfaContext and route
        // to /verify-mobile (if no number on file) or /two-factor-authentication
        // (OTP entry). This unmounts the modal and leaves the auth completion
        // to the existing MFA pages.
        if (data && data.pendingMFA === true) {
          const userDetails = data.userDetails;
          const hasMobile =
            userDetails && userDetails.mobileNumber ? true : false;
          const isMFAEnabled =
            userDetails &&
            userDetails.organizationId &&
            userDetails.organizationId.mfa &&
            userDetails.organizationId.mfa.isEnabled
              ? true
              : false;
          const isAdmin =
            userDetails && userDetails.role === "Admin" ? true : false;
          if (!hasMobile && isMFAEnabled && isAdmin) {
            localStorage.setItem(
              "mfaContext",
              JSON.stringify({ email: userDetails.email })
            );
            if (history) {
              history.push("/verify-mobile");
            }
            return;
          }
          if (hasMobile && isMFAEnabled && isAdmin) {
            localStorage.setItem(
              "mfaContext",
              JSON.stringify({ email: data.email })
            );
            if (history) {
              history.push("/two-factor-authentication");
            }
            return;
          }
          setErrorMessage(TRIVIA_LOGIN_INVALID_CREDENTIALS);
          setIsLoading(false);
          return;
        }
        const userDetails = data && data.userDetails;
        const token = data && data.autherizationToken;
        if (!userDetails || !token) {
          setErrorMessage(TRIVIA_LOGIN_INVALID_CREDENTIALS);
          setIsLoading(false);
          return;
        }
        saveToken(token);
        onLoginSuccess({
          ...userDetails,
          autherizationToken: token,
        });
        setIsLoading(false);
        if (onAuthSuccess) {
          onAuthSuccess();
        }
      })
      .catch((error) => {
        const responseData = error && error.response && error.response.data;
        let message = TRIVIA_LOGIN_INVALID_CREDENTIALS;
        if (responseData && typeof responseData === "string") {
          message = responseData;
        } else if (
          responseData &&
          responseData.errors &&
          responseData.errors.message
        ) {
          message = responseData.errors.message;
        }
        setErrorMessage(message);
        setIsLoading(false);
      });
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (!phoneNumber) {
      return;
    }
    setIsLoading(true);
    setErrorMessage(null);

    axios
      .post(
        url.sendTriviaLoginCode,
        { phone: phoneNumber, orgId: orgId },
        {
          headers: {
            Authorization: "Bearer basicauth",
          },
        }
      )
      .then(() => {
        setIsLoading(false);
        setStep(STEP_PHONE_CODE);
      })
      .catch((error) => {
        const status = error && error.response && error.response.status;
        if (status === 404) {
          setErrorMessage(TRIVIA_LOGIN_NOT_INTENDED);
        } else {
          const responseData = error && error.response && error.response.data;
          let message = TRIVIA_LOGIN_GENERIC_ERROR;
          if (
            responseData &&
            responseData.errors &&
            responseData.errors.message
          ) {
            message = responseData.errors.message;
          }
          setErrorMessage(message);
        }
        setIsLoading(false);
      });
  };

  const handleVerifyCode = (verificationCode) => {
    if (!verificationCode || verificationCode.length !== 4) {
      setErrorMessage(TRIVIA_LOGIN_INVALID_CODE);
      return;
    }
    setIsLoading(true);
    setErrorMessage(null);

    axios
      .post(
        url.verifyTriviaLoginCode,
        { phone: phoneNumber, code: verificationCode, orgId: orgId },
        {
          headers: {
            Authorization: "Bearer basicauth",
          },
        }
      )
      .then((response) => {
        const data = response && response.data;
        const userDetails = data && data.userDetails;
        const token = data && data.autherizationToken;
        if (!userDetails || !token) {
          setErrorMessage(TRIVIA_LOGIN_INVALID_CODE);
          setIsLoading(false);
          return;
        }
        saveToken(token);
        onLoginSuccess({
          ...userDetails,
          autherizationToken: token,
        });
        setIsLoading(false);
        if (onAuthSuccess) {
          onAuthSuccess();
        }
      })
      .catch((error) => {
        const responseData = error && error.response && error.response.data;
        let message = TRIVIA_LOGIN_INVALID_CODE;
        if (
          responseData &&
          responseData.errors &&
          responseData.errors.message
        ) {
          message = responseData.errors.message;
        }
        setErrorMessage(message);
        setIsLoading(false);
      });
  };

  const renderError = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div className="trivia-login-modal__error" role="alert">
        {errorMessage}
      </div>
    );
  };

  const renderBackButton = () => {
    return (
      <button
        type="button"
        className="trivia-login-modal__back-btn"
        onClick={goToChoose}
        disabled={isLoading}
      >
        {TRIVIA_LOGIN_BACK}
      </button>
    );
  };

  const renderChooseStep = () => {
    return (
      <div>
        <button
          type="button"
          className="trivia-login-modal__option-btn"
          onClick={goToEmail}
        >
          {TRIVIA_LOGIN_OPTION_EMAIL}
        </button>
        <button
          type="button"
          className="trivia-login-modal__option-btn trivia-login-modal__option-btn--secondary"
          onClick={goToPhone}
        >
          {TRIVIA_LOGIN_OPTION_PHONE}
        </button>
      </div>
    );
  };

  const renderEmailStep = () => {
    const isDisabled = isLoading || !validateEmail(email) || !password;
    return (
      <Form onSubmit={handleEmailSubmit} noValidate>
        <div className="trivia-login-modal__form-group">
          <label className="trivia-login-modal__label" htmlFor="trivia-login-email">
            {TRIVIA_LOGIN_EMAIL_LABEL}
          </label>
          <Input
            id="trivia-login-email"
            type="email"
            placeholder={TRIVIA_LOGIN_EMAIL_PLACEHOLDER}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            autoComplete="email"
          />
        </div>
        <div className="trivia-login-modal__form-group">
          <label className="trivia-login-modal__label" htmlFor="trivia-login-password">
            {TRIVIA_LOGIN_PASSWORD_LABEL}
          </label>
          <Input
            id="trivia-login-password"
            type="password"
            placeholder={TRIVIA_LOGIN_PASSWORD_PLACEHOLDER}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            autoComplete="current-password"
          />
        </div>
        {renderError()}
        <button
          type="submit"
          className="trivia-login-modal__primary-btn"
          disabled={isDisabled}
        >
          {isLoading ? "Submitting..." : TRIVIA_LOGIN_SUBMIT}
        </button>
        {renderBackButton()}
      </Form>
    );
  };

  const renderPhoneStep = () => {
    const isDisabled = isLoading || !phoneNumber;
    return (
      <Form onSubmit={handlePhoneSubmit} noValidate>
        <div className="trivia-login-modal__form-group">
          <label className="trivia-login-modal__label" htmlFor="trivia-login-phone">
            {TRIVIA_LOGIN_PHONE_LABEL}
          </label>
          <PhoneInputWithCountrySelect
            id="trivia-login-phone"
            placeholder={TRIVIA_LOGIN_PHONE_PLACEHOLDER}
            value={phoneNumber}
            onChange={setPhoneNumber}
            defaultCountry="US"
            disabled={isLoading}
          />
        </div>
        {renderError()}
        <button
          type="submit"
          className="trivia-login-modal__primary-btn"
          disabled={isDisabled}
        >
          {isLoading ? "Sending..." : TRIVIA_LOGIN_SEND_CODE}
        </button>
        {renderBackButton()}
      </Form>
    );
  };

  const renderPhoneCodeStep = () => {
    return (
      <div>
        <VerificationCodeEntry
          phoneNumber={phoneNumber}
          onVerify={handleVerifyCode}
          onResendCode={() =>
            handlePhoneSubmit({ preventDefault: () => {} })
          }
          onChangeNumber={goToPhone}
          isLoading={isLoading}
        />
        {renderError()}
      </div>
    );
  };

  const renderBody = () => {
    if (step === STEP_EMAIL) return renderEmailStep();
    if (step === STEP_PHONE) return renderPhoneStep();
    if (step === STEP_PHONE_CODE) return renderPhoneCodeStep();
    return renderChooseStep();
  };

  const getSubtitle = () => {
    if (step === STEP_EMAIL) return TRIVIA_LOGIN_MODAL_SUBTITLE_EMAIL;
    if (step === STEP_PHONE) return TRIVIA_LOGIN_MODAL_SUBTITLE_PHONE;
    if (step === STEP_PHONE_CODE) return TRIVIA_LOGIN_MODAL_SUBTITLE_CODE;
    return TRIVIA_LOGIN_MODAL_SUBTITLE_CHOOSE;
  };

  return (
    <Modal
      isOpen={isOpen}
      backdrop="static"
      keyboard={false}
      centered
      className="trivia-login-modal"
    >
      <ModalHeader tag="div">
        <div className="trivia-login-modal__title">
          {TRIVIA_LOGIN_MODAL_TITLE}
        </div>
        <div className="trivia-login-modal__subtitle">{getSubtitle()}</div>
      </ModalHeader>
      <ModalBody>{renderBody()}</ModalBody>
    </Modal>
  );
};

const mapDispatchToProps = (dispatch) => {
  return {
    onLoginSuccess: (data) => dispatch(actionCreators.loginSuccess(data)),
  };
};

export default connect(null, mapDispatchToProps)(TriviaLoginModal);
